import { IApiaForm } from '../../../../JSApi/types';
import store from '../../../../store';
import { buttonsActions } from '../../../../store/buttonsSlice';
import { TApiaForm } from '../../../../types';
import { customEvents } from '../../../../utils/dom';
import { arrayOrArray } from '../../../../utils/util';
import EventEmitter from '../../../../utils/util/eventEmitter';
import {
  forceWhiteScreen,
  releaseScreenLock,
} from '../../../common/dialogs/modals/ScreenLock';
import TabsController from '../../../common/tabs/TabsController';
import { callScriptFunction } from '../fieldsStore/hooks/useScriptEvents';
import { setHasChangedAnyField } from '../fieldsStore/MakeField';

export interface IFormRegister {
  formId: string;
  formData: TApiaForm;
  hasLoaded: boolean;
  type: 'E' | 'P';
}

declare global {
  interface Window {
    IS_RELOAD: boolean;
  }
}

type TFormEventTypes =
  | 'onLoad'
  | 'onSubmit'
  | 'onReload'
  | 'onBeforePrint'
  | 'onAfterPrint';

export const formsEventsControllerEvents = new (class extends EventEmitter<{
  finishedOnLoad: void;
}> {})();
const formsEventsController = new (class FormsEventsController {
  initialized = false;

  constructor() {
    const handleBeforePrint = () => {
      void this.#internalFireEvents('onBeforePrint');
    };
    const handleAfterPrint = () => {
      void this.#internalFireEvents('onAfterPrint');
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
  }

  #timeout: number = -1;
  init() {
    TabsController.setOnApiaUtil();

    clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      if (!this.initialized) {
        this.initialized = true;
        store.dispatch(
          buttonsActions.updateAction([
            { disabled: true, action: 'confirm' },
            { disabled: true, action: 'next' },
          ]),
        );
        void this.#checkIfShouldFireLoadEvents();
      }
    }, 100) as any;
  }

  /**
   * Ejecuta los eventos de formularios correspondientes al nombre pasado como
   * parámetro.
   *
   * @param eventName Nombre del evento a lanzar, uno de TFormEventTypes
   * @returns Una promesa, se resuelve en verdadero si todo sale bien
   */
  fireEvents(eventName: TFormEventTypes) {
    return this.#internalFireEvents(eventName);
  }

  /**
   * Cada formulario debe reportarse como cargado una vez que todos sus campos
   * se mostraron en pantalla, para ello llaman a este método.
   */
  markAsLoaded(formId: string) {
    this.#updateForm(formId, { hasLoaded: true });
    void this.#checkIfShouldFireLoadEvents();
  }

  sortedForms: string[] = [];

  /**
   * Cada formulario que deba participar en el proceso de ejecución de eventos
   * debe registrarse mediante este método. Está hecho así y no son cargados
   * directamente por el controlador ya que hay lógicas particulares que
   * podrían evitar que algún formulario se registre. Con esa consideración se
   * deja la responsabilidad de decidir al formulario donde esa lógica ya está
   * implementada.
   */
  registerForm(registerData: Omit<IFormRegister, 'hasLoaded' | 'type'>) {
    this.sortedForms = [...this.sortedForms, registerData.formId];

    this.#updateForm(registerData.formId, {
      ...registerData,
      hasLoaded: false,
      type: registerData.formId.charAt(0) as 'E' | 'P',
    });
  }

  #forms: Record<string, IFormRegister> = {};

  hasFiredLoadEvents = false;
  async #checkIfShouldFireLoadEvents() {
    const formsArray = Object.values(this.#forms);
    if (
      formsArray.filter((form) => form.hasLoaded).length === formsArray.length
    ) {
      if (this.hasFiredLoadEvents) {
        store.dispatch(buttonsActions.setState({ disabledActions: [] }));
        return;
      }
      this.hasFiredLoadEvents = true;
      forceWhiteScreen();
      for (const form of Object.values(this.#forms)) {
        const mapEvent = window[`LOAD_RELOAD_MAP_${form.type}`];
        const mappedEvent = mapEvent
          ? mapEvent[form.formData.formName]
          : undefined;
        const actualMapEvent =
          mapEvent && (mappedEvent === 'load' ? 'onLoad' : 'onReload');
        const eventType: TFormEventTypes =
          actualMapEvent || (window.IS_RELOAD ? 'onReload' : 'onLoad');

        const hasFiredCorrectly = await this.#internalFireEvents(
          form,
          eventType,
        );
        if (!hasFiredCorrectly) break;
      }
      releaseScreenLock();
      store.dispatch(buttonsActions.setState({ disabledActions: [] }));
      formsEventsControllerEvents.emit('finishedOnLoad');
      window.dispatchEvent(new Event(customEvents.formsReady));
      setHasChangedAnyField(false);
    }
  }

  async #internalFireEvents(eventName: TFormEventTypes): Promise<boolean>;
  async #internalFireEvents(
    formRegister: IFormRegister | TFormEventTypes,
    eventName: TFormEventTypes,
  ): Promise<boolean>;
  async #internalFireEvents(
    formRegister: IFormRegister | TFormEventTypes,
    eventName?: TFormEventTypes,
  ): Promise<boolean> {
    const actualFormRegister =
      typeof formRegister === 'string'
        ? Object.values(this.#forms)
        : formRegister;
    const actualEventName =
      typeof formRegister === 'string' ? formRegister : eventName;

    let hasSucceed = true;

    for (const currentFormRegister of arrayOrArray(actualFormRegister)) {
      for (const functionDefinition of currentFormRegister.formData.scriptEvents?.filter(
        (event) => event.evtName === actualEventName,
      ) ?? []) {
        const formSource = window.ApiaFunctions.getForm(
          currentFormRegister.formData.formName,
        ) as IApiaForm;
        hasSucceed =
          hasSucceed &&
          (await callScriptFunction(
            formSource,
            formSource,
            functionDefinition,
          ));
        if (!hasSucceed) break;
      }
    }
    return hasSucceed;
  }

  #updateForm(id: string, newData: Partial<IFormRegister>) {
    const currentValue = this.#forms[id] ?? {};
    this.#forms[id] = { ...currentValue, ...newData };
  }
})();

export default formsEventsController;
